import clsx from 'clsx';
import * as styles from '../styles/containers/UserEditor.less';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import debounce from 'lodash/debounce';
import Select from 'react-select';
import { withTranslation, WithTranslation } from "react-i18next";
import { DASHBOARD_URL, PASSWORD_MIN_LENGTH } from '../constants/config';
import Error from '../components/common/Error';
import Loader from '../components/common/Loader';
import DashboardTargets from '../components/dashboard/DashboardTargets'
import CustomerDashboards from '../components/dashboard/CustomerDashboards';
import SuperuserIsActive from './SuperuserIsActive';
import CopyToClipboard from '../components/common/CopyToClipboard';
import ConfirmationDialog from '../components/common/ConfirmationDialog';
import { useActions } from '../hooks/useActions';
import { useAppSelector } from '../store/hooks';
import type { EntityId } from '../types/models';

interface CustomerOption {
  value: EntityId;
  label: string;
}

interface UserEditorOwnProps {
  className?: string;
}

const UserEditor = (props: UserEditorOwnProps & WithTranslation) => {
  const { t } = props; // [t, i18n]
  const actions = useActions();
  const params = useParams();
  const navigate = useNavigate();

  // Store values (source of truth for save + validation).
  const userNameStore = useAppSelector((store) => store.user.userName || '');
  const email = useAppSelector((store) => store.user.email);
  const password = useAppSelector((store) => store.user.password);
  const disabled = useAppSelector((store) => store.user.disabled || false);
  const customerId = useAppSelector((store) => store.user.customerId || 1);
  const customerName = useAppSelector((store) => store.user.customerName || '');
  const selectedTargets = useAppSelector((store) => store.user.selectedTargets);
  const selectedDashboards = useAppSelector((store) => store.user.selectedDashboards);
  // tree slice is plain now (Phase 4 de-Immutable). Memoised only to keep a stable
  // ref when the store value is falsy (DashboardTargets consumes plain data).
  const userTargetsRaw = useAppSelector((store) => store.tree.targets);
  const userTargets = useMemo(() => userTargetsRaw || [], [userTargetsRaw]);
  const users = useAppSelector((store) => store.users.users || []);
  const isUserLoading = useAppSelector((store) => store.user.isUserLoading);
  const isTreeLoading = useAppSelector((store) => store.tree.isTreeLoading);
  const isDashboardsLoading = useAppSelector((store) => store.customer.isDashboardsLoading);
  const isUserValid = useAppSelector((store) => store.user.isUserValid || false);
  const error = useAppSelector((store) => store.user.error);
  const errorMessage = useAppSelector((store) => store.user.errorMessage);
  const isAuthenticated = useAppSelector((store) => store.auth.isAuthenticated);
  const isSuperuser = useAppSelector((store) => store.auth.isSuperuser);
  const isSuperuserActive = useAppSelector((store) => store.auth.isSuperuserActive);

  // Render-driving local state (these updated instance fields in the class but
  // never re-rendered — the stale-UI bug; now proper state).
  const [showDialog, setShowDialog] = useState(false);
  const [userId, setUserId] = useState<EntityId | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState<EntityId>(0);
  const [selectedCustomerName, setSelectedCustomerName] = useState('');
  const [isDublicateEmail, setIsDublicateEmail] = useState(false);
  const [isValidPassword, setIsValidPassword] = useState(false);
  const [userCustomers, setUserCustomers] = useState<CustomerOption[]>([]);

  // Uncontrolled-input backing values (defaultValue) + lookup data.
  const userNameRef = useRef('');
  const contactNameRef = useRef('');
  const emailRef = useRef('');
  const passwordRef = useRef('');
  // The dropdown's current option. This used to be built inline from the USER
  // record's customerName — so whenever that field was empty or stale the label
  // rendered blank, which is why the customer showed only sometimes. The id is
  // reliable, so look the name up in the loaded customer list and fall back to
  // the stored name only if the list has not arrived (or lacks that id); null
  // lets react-select show its placeholder instead of an empty selection.
  const selectedCustomerOption = userCustomers.find(c => c.value === selectedCustomerId)
    ?? (selectedCustomerName ? { label: selectedCustomerName, value: selectedCustomerId } : null);

  const usersRef = useRef<any[]>([]);
  const targetsRootRef = useRef<any>(undefined);

  // Seeds the tickbox. Not from the store: it still holds the previous user's
  // value on first render, and the tickbox can't be corrected after mount.
  const disabledRef = useRef(false);

  const editUser = useMemo(() => debounce(actions.editUser, 250), [actions]);
  useEffect(() => () => editUser.cancel(), [editUser]);
  const fastEditUser = actions.editUser;

  // componentDidMount: load the user (existing) or set up a new one.
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    actions.resetUser();
    console.log(isSuperuser ? 'Your role: Superuser' : 'Your role: Admin');
    actions.resetCustomer();
    actions.resetTree();
    // All customers (for the dropdown).
    actions.fetchUserCustomers().then((cust: any) => {
      setUserCustomers(cust.payload.map((c: any) => ({ value: c.id, label: c.name })));
    });

    const uid = params.userId ?? ''; // auth0 uid
    if (uid.length > 4) { // existing user
      actions.fetchUser(uid).then((data: any) => {
        const user = data.payload;
        setUserId(user.uid);
        userNameRef.current = user.name;
        emailRef.current = user.email;
        passwordRef.current = user.password;
        setSelectedCustomerId(user.customerId);
        setSelectedCustomerName(user.customerName || '');
        fastEditUser('userName', user.name, true); // true === skip validation
        fastEditUser('email', user.email, true);
        fastEditUser('password', user.password, true);
        disabledRef.current = user.disabled === true;
        fastEditUser('disabled', user.disabled === true, true);
        fastEditUser('customerId', user.customerId, true);
        fastEditUser('customerName', user.customerName || '');
        if (user.customerId) {
          if (isSuperuserActive) {
            actions.fetchUserTargetsRoot(user.customerId).then((root: any) => {
              actions.fetchTree(root.payload._id);
            });
          }
          actions.fetchUserDashboards(user.customerId);
        }
      });
    } else { // create new user
      setUserId(0);
      usersRef.current = users || []; // users is a plain array now (M5)
      if (!usersRef.current.length) {
        actions.fetchUsers('*').then((response: any) => { // all users for duplicate email checks
          usersRef.current = Object.values(response.payload);
        });
      }
      fastEditUser('userName', '');
    }
    const element = document.getElementById('new-user');
    if (element) {
      element.focus();
    }
  }, [isAuthenticated, params.userId, actions]); // eslint-disable-line react-hooks/exhaustive-deps

  const onTargetToggle = (targetId: string) => actions.toggleSelectedRoot(targetId);
  const onDashboardToggle = (dashboardId: string) => actions.toggleSelectedDashboard(dashboardId);
  const refreshPage = () => window.location.reload();

  const onSaveUser = () => {
    const uid = params.userId ?? '';
    const user = {
      uid: uid,
      name: userNameStore,
      email: email,
      password: password,
      disabled: disabled,
      customerId: customerId,
      customerName: customerName,
      targets: selectedTargets,
      dashboards: selectedDashboards,
    };
    if (uid !== '0') {
      actions.updateUser(user).then(() => navigate('/user/' + uid));
    } else {
      actions.saveNewUser(user).then((response: any) => { // Auth0 + Mongo
        navigate('/user/' + response.payload.uid);
        refreshPage();
      });
    }
  };

  const onHandleDeleteClick = () => setShowDialog(true);

  const onHandleConfirm = () => {
    if (!disabled) {
      return;
    }
    const uid = params.userId ?? '';
    actions.deleteUser(uid, disabled).then(() => navigate('/'));
    setShowDialog(false);
  };

  const onHandleCancel = () => setShowDialog(false);

  const onChangeUserName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value.trim();
    userNameRef.current = name;
    editUser('userName', name.trim());
  };

  // *** TODO *** Remove if not used
  const onChangeContactName = (e: React.ChangeEvent<HTMLInputElement>) => editUser('userName', e.target.value.trim());

  // Handle email change and check it's unique.
  const onChangeEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    let duplicate = false;
    for (let i = 0, ilen = usersRef.current.length; i < ilen; i++) {
      if (value === usersRef.current[i].email) {
        duplicate = true;
        break;
      }
    }
    setIsDublicateEmail(duplicate);
    editUser('email', value);
  };

  // User password simple sanity check.
  const onChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    setIsValidPassword(value.length >= PASSWORD_MIN_LENGTH);
    editUser('password', value);
  };

  // Not by id lookup: tickbox_disabled is reused in other editors.
  const onChangeDisabled = (e: React.ChangeEvent<HTMLInputElement>) => {
    disabledRef.current = e.target.checked;
    fastEditUser('disabled', e.target.checked);
  };

  // Handle user customer change — load new customer tree if it changed or is void.
  const onChangeUserCustomerName = (e: any) => {
    const id = e.value;
    const name = e.label;
    if (id !== selectedCustomerId || !targetsRootRef.current) {
      actions.fetchUserTargetsRoot(id).then((root: any) => {
        actions.fetchTree(root.payload._id);
      });
      actions.fetchUserDashboards(id);
    }
    setSelectedCustomerId(id);
    setSelectedCustomerName(name);
    fastEditUser('customerName', name);
    fastEditUser('customerId', id);
  };

  // Tells why Save is disabled.
  let saveHint;
  if (isDublicateEmail) {
    saveHint = 'That email is already in use';
  } else if (!isUserValid) {
    saveHint = 'Needs a name, a valid email, a password and a customer';
  }

  const uid = params.userId || false;
  // For an existing user we know from the URL that a fetch is coming, so show the
  // Loader from the first render until the data arrives (userId is set in the fetch
  // callback) — otherwise the empty form paints for one frame before loading starts.
  const isExistingUser = (params.userId ?? '').length > 4;

  if (error) {
    return <Error message={errorMessage} />;
  }
  if (isUserLoading || (isExistingUser && userId === null)) {
    return <Loader style={{ padding: "2em", textAlign: "center" }} className={props.className} />;
  }

  return (
    <div className={styles['user--editor']} >
      <h2>{t('user.editor.name')}
        {isUserLoading &&
          <Loader style={{ display: 'inline-block', fontSize: '0.5em' }} />}
      </h2>
      <div className={styles.container}>
        <label>{t('main.customer')}</label>
        <div>
          <Select
            id="select_customer"
            placeholder={userId ? 'Select to change...' : 'Select a customer (required)...'}
            value={selectedCustomerOption}
            options={userCustomers}
            onChange={onChangeUserCustomerName}
            isDisabled={!isSuperuserActive}
          />
        </div>
      </div>

      <div className={styles.container}>
        <label>{t('main.name')}</label>
        <div>
          <input id="new-user"
            className={styles.input}
            type="text"
            placeholder="User name"
            defaultValue={userNameRef.current}
            onChange={onChangeUserName}
          />
        </div>
      </div>

      <label className={styles.hidden}>{t('main.contact')}</label>
      <div className={styles.hidden}>
        <input
          className={styles.input}
          type="text"
          placeholder="Contact name"
          defaultValue={contactNameRef.current}
          onChange={onChangeContactName}
        />
        <a className={uid ? styles['user--link'] : styles.hidden}
          href={`${DASHBOARD_URL}?hash=${uid}`}
          title="Open user home page"
          target="_blank"><i className="fa fa-lg fa-external-link" aria-hidden="true"></i></a>
      </div>

      <div className={styles.container}>
        <label>{t('main.email')}</label>
        <div>
          <input
            className={styles.input}
            type={'email'}
            placeholder="User email"
            defaultValue={emailRef.current}
            onChange={onChangeEmail}
            style={{ color: isDublicateEmail ? "red" : "black" }}
            disabled={!!userId}
          />
        </div>
      </div>

      <SuperuserIsActive>
        <div className={styles.container} style={{ display: !userId ? "none" : "flex" }}>
          <label>{t('user.editor.auth0uid')}</label>
          <div>
            <CopyToClipboard
              textString={userId === undefined || userId === null ? undefined : String(userId)}
              style={{ width: "50%", borderRadius: "3px", background: "lightGrey", resize: "none", textAlign: "center", color: "grey", marginRight: "5px" }}
            />
          </div>
        </div>
      </SuperuserIsActive>
      <div className={clsx(styles.container, !userId ? props.className : 'hidden')} >
        <label>{t('main.password')}</label>
        <div>
          <input
            className={styles.input}
            type={'text'}
            placeholder="User password"
            defaultValue={passwordRef.current}
            onChange={onChangePassword}
            style={{ color: isValidPassword ? "black" : "red" }}
          />
        </div>
      </div>

      <div className={styles.container}>
        <label></label>
        <div className={clsx(styles.container, 'chartSelector')} >
          <input
            id="tickbox_disabled"
            type={'checkbox'}
            title="Disable user access"
            defaultChecked={disabledRef.current}
            onChange={onChangeDisabled}
          />
          <label>{t('user.editor.disabled')}</label>
        </div>
      </div>

      <div style={{ display: "flex" }}>
        <button className={clsx(styles.save, 'button success')}
          onClick={onSaveUser}
          title={saveHint}
          disabled={!isUserValid || isDublicateEmail}
          >{t('button.save')}</button>
        <SuperuserIsActive>
          <button className={clsx(styles['delete-button'], 'button alert')}
            onClick={onHandleDeleteClick}
            disabled={!isUserValid || !disabled}
            >{t('button.delete')}</button>

          {showDialog && (
            <ConfirmationDialog
              message={t('user.editor.confirmDelete') + userNameStore + "?"}
              confirmText={t('button.yes')}
              cancelText={t('button.no')}
              onConfirm={onHandleConfirm}
              onCancel={onHandleCancel}
            />
          )}
        </SuperuserIsActive>
      </div>
      <div className="pure-u-24-24">
        <SuperuserIsActive>
          <div className="pure-u-12-24">
            <h2>{t('target.editor.available')}
              {isUserLoading &&
                <Loader style={{ display: 'inline-block', fontSize: '0.5em' }} />}
            </h2>
            <DashboardTargets
              targets={userTargets}
              selectedTargets={selectedTargets}
              onTargetToggle={onTargetToggle}
              isLoading={isTreeLoading}
            />
          </div>
        </SuperuserIsActive>
        <div className="pure-u-12-24">
          <h2>{t('dashboard.editor.available')}
            {isUserLoading &&
              <Loader style={{ display: 'inline-block', fontSize: '0.5em' }} />}
          </h2>
          <CustomerDashboards
            selectedDashboards={selectedDashboards}
            onDashboardToggle={onDashboardToggle}
            isLoading={isDashboardsLoading}
          />
        </div>
      </div>
    </div>
  );
};

const TranslatedUserEditor = withTranslation('common')(UserEditor);

// Remount per user; otherwise the previous user's values stay in the inputs.
const UserEditorRoute = (props: UserEditorOwnProps) => {
  const { userId } = useParams();
  return <TranslatedUserEditor key={userId} {...props} />;
};

export default UserEditorRoute;
